import { computed } from 'vue';
import { queryOptions, useQuery } from '@tanstack/vue-query';
import type {
  IUserInfoResponse,
  IUserProviderResponse,
  IUpdateUser,
  IUpdateUserSelfParameters,
  ITeamResponse,
  ITeamParameter,
  IUpdateTeamParameter,
  UserSubTeamResponseDTO,
} from '@/api-v2/web/user';
import {
  getUserInfo,
  getUserProvider,
  getTeamInfo,
  getSubTeams,
  getTeamParameters,
  updateTeamParameters,
} from '@/api-v2/web/user';
import type { VQQueryOptions } from '@/types/VueQueryTypes';
import useUser from '../useUser';
import { defineMutation } from './defineMutation';
import { chainEnabled } from '@/utils/queries';
import { limitedQueryFn } from './useLimitedQueries';
import { isAxiosError } from 'axios';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';
import { UserModule } from '@/store/modules/user';
import { UserPermission } from '@/constants/UserPermission';
import { userInfoQueryKey } from '@/auth/shared/userInfoQueryKey';

export const userQueryKeys = {
  all: () => [{ scope: 'user' }] as const,
  user: () => userInfoQueryKey,
  userProvider: () => [{ ...userQueryKeys.all()[0], entity: 'user-provider' }] as const,
  teamInfo: () => [{ ...userQueryKeys.all()[0], entity: 'team-info' }] as const,
  teamParameters: () => [{ ...userQueryKeys.all()[0], entity: 'team-parameters' }] as const,
  subteams: () => [{ ...userQueryKeys.all()[0], entity: 'sub-teams' }] as const,
};

const select = {
  listTeamMembers: (data: ITeamResponse) => {
    return new Map(data.users.map((user) => [user.id, user]));
  },
};

/**
 * Like all our other queries, do not refetch this query when the tab regains focus.
 *
 * `refetchOnWindowFocus` runs in the same turn Chrome unfreezes the tab, before the first paint.
 * This query is observed by auth, the navbar, settings, and discover lists, so that refetch
 * re-renders the whole page and the tab stays blank for seconds.
 *
 * Session expiry is checked by the visibility probe in `useSessionProbe` (started from
 * `useBlockedRedirect`, so HSBC and RBC are included). A 200 is not written into this cache.
 */
export function useUserQuery(options: VQQueryOptions<IUserInfoResponse> = {}) {
  return useQuery({
    ...options,
    queryKey: userQueryKeys.user(),
    queryFn: ({ signal }) => getUserInfo(signal),
    retry(failureCount, error) {
      // Do not retry if this is a 4xx. We rely on this query to check if the
      // user is authenticated or blocked. Retrying won't make any difference
      // but make user stuck in pending page longer.
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status != null && status >= 400 && status < 500) {
          return false;
        }
      }

      return failureCount < 3;
    },
  });
}

/**
 * Like all our other queries, do not refetch this query when the tab regains focus.
 *
 * `refetchOnWindowFocus` runs in the same turn Chrome unfreezes the tab, before the first paint.
 * This query is observed by auth, the navbar, settings, and discover lists, so that refetch
 * re-renders the whole page and the tab stays blank for seconds.
 *
 * Session expiry is checked by the visibility probe in `useSessionProbe` (started from
 * `useBlockedRedirect`, so HSBC and RBC are included). A 200 is not written into this cache.
 */
export function useUserQueryOptions(options: VQQueryOptions<IUserInfoResponse> = {}) {
  return queryOptions({
    ...options,
    queryKey: userQueryKeys.user(),
    queryFn: ({ signal }) => getUserInfo(signal),
    retry(failureCount, error) {
      // Do not retry if this is a 4xx. We rely on this query to check if the
      // user is authenticated or blocked. Retrying won't make any difference
      // but make user stuck in pending page longer.
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status != null && status >= 400 && status < 500) {
          return false;
        }
      }

      return failureCount < 3;
    },
  });
}

export function useUserProviderQuery(options: VQQueryOptions<IUserProviderResponse> = {}) {
  return useQuery({
    queryKey: userQueryKeys.userProvider(),
    queryFn: ({ signal }) => getUserProvider(signal),
    ...options,
  });
}

export function useTeamInfo(options: VQQueryOptions<ITeamResponse> = {}) {
  return useQuery({
    queryKey: userQueryKeys.teamInfo(),
    queryFn: ({ signal }) => getTeamInfo(signal),
    ...options,
  });
}

export function useTeamParameters(options: VQQueryOptions<ITeamParameter> = {}) {
  return useQuery<ITeamParameter, Error>({
    queryKey: userQueryKeys.teamParameters(),
    queryFn: limitedQueryFn(({ signal }) => getTeamParameters(signal)),
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

/**
 * Get a map of user ID -> user that has the same team with the current
 * authenticated user
 */
export function useListTeamMembers(options: VQQueryOptions = {}) {
  const enabled = chainEnabled(options.enabled);

  return useLoadingWhenEnabledQuery(
    useQuery({
      ...options,
      queryKey: userQueryKeys.teamInfo(),
      queryFn: ({ signal }) => getTeamInfo(signal),
      select: select.listTeamMembers,
      enabled,
    }),
    enabled,
  );
}

export const useUpdateUserMutation = defineMutation({
  mutationFn: (data: IUpdateUser) => UserModule.UpdateUserInfo(data),
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: userQueryKeys.user() });
  },
});

export const useUpdateUserSelfParametersMutation = defineMutation({
  mutationFn: (data: IUpdateUserSelfParameters) => UserModule.UpdateUserSelfParameters(data),
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: userQueryKeys.user() });
  },
});

export const useUpdateTeamParameters = defineMutation({
  mutationFn: (data: IUpdateTeamParameter) => updateTeamParameters(data),
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: userQueryKeys.teamParameters() });
  },
});

export function useSubTeamList(options: VQQueryOptions<UserSubTeamResponseDTO> = {}) {
  const { user } = useUser();

  return useQuery({
    queryKey: userQueryKeys.subteams(),
    queryFn: ({ signal }) => getSubTeams(signal),
    ...options,
    enabled: chainEnabled(
      options.enabled,
      import.meta.env.VITE_CLIENT === 'Premialab', // only sub-teams supported on core platform
      computed(
        () =>
          user.value != null &&
          // The IMPERSONATE_TEAM permission is set when the user is actively impersonating another team.
          // TODO: rename this confusing enum name (along with other confusing enum names)
          (user.value.team.isParentTeam || user.value.permission.includes(UserPermission.IMPERSONATE_TEAM)),
      ),
    ),
  });
}
